import { sendRiskifiedBeaconAddToCart } from '@catalogo/core-send-beacon';
import { Cookies } from '@catalogo/core-cookies';
import { IOffer, IProduct, IProductCache, ICartLine, IService } from '../types/global';
import {
  ISetInitialCartIdProps,
  ICartMutationResult,
  IAddProductToCartProps,
  IOffersToAdd,
  IFmtLinesToAddInCartProps,
  IFmtOffersToAddInCartProps,
  ITypeOfProduct,
} from '../types/add-to-cart';

export const CART_KEY = 'cart.id';

export const setInitialCartId = ({ cookies, setCookie, refetchCart, setStateComplete }: ISetInitialCartIdProps) => (
  cartResult: ICartMutationResult
) => {
  const cardIdFromCookies = cookies?.[CART_KEY];
  const cartIdFromResult = cartResult?.addCartLines?.cartId;

  if (
    (!cardIdFromCookies && !!cartIdFromResult) ||
    (cardIdFromCookies && !!cartIdFromResult && cartIdFromResult !== cardIdFromCookies)
  ) {
    setCookie(CART_KEY, cartIdFromResult);
  }

  setStateComplete(true);
  refetchCart();
};

export const getCartLinesMap = (cartLines?: ICartLine[]) => {
  const cartLinesMap = new Map<string, ICartLine>();
  cartLines?.map((line: ICartLine) => {
    cartLinesMap.set(line?.offer?.id as string, line);
  });

  return cartLinesMap;
};

export const fmtOffersToAddInCart = ({
  offers,
  sellerStoreId,
  cartLinesMap,
  servicePDPinfoForWarranty,
}: IFmtOffersToAddInCartProps) =>
  offers?.map((offer: IOffer) => {
    const line = cartLinesMap?.get(offer?.offerId as string);
    const serviceForWarranty = servicePDPinfoForWarranty as IService;
    const services =
      !line && !!serviceForWarranty && serviceForWarranty?.productSku === offer?.productSku ? [serviceForWarranty] : [];
    return {
      lineId: line?.id,
      offerId: offer?.offerId,
      quantity: offer?.quantity,
      services,
      sellerStoreId,
    };
  });

export const fmtLinesToAddInCart = ({
  offers,
  cartLines,
  sellerStoreId,
  servicePDPinfoForWarranty,
}: IFmtLinesToAddInCartProps) => {
  const cartLinesMap = getCartLinesMap(cartLines);

  // pego todas as ofertas que estao no cart mas que possuem quantidade diferentes das ofertas que estao sendo recebidas
  const offersToUpdate = offers?.filter((offer: IOffer) => {
    const offerInCart = cartLinesMap.get(offer?.offerId as string);
    const offerConditions =
      offer?.quantity !== offerInCart?.offer?.quantity ||
      (!!sellerStoreId && offerInCart?.sellerStoreId !== sellerStoreId);
    return !!offerInCart && offerConditions;
  });

  // pego todas as ofertas que não estão no cart
  const offersToAdd = offers?.filter((offer: IOffer) => {
    const isOfferInCart = !!cartLinesMap.get(offer?.offerId as string);
    return !isOfferInCart;
  });

  return {
    offersToAdd: fmtOffersToAddInCart({ offers: offersToAdd, sellerStoreId, servicePDPinfoForWarranty }),
    offersToUpdate: fmtOffersToAddInCart({ offers: offersToUpdate, sellerStoreId, cartLinesMap }),
  };
};

export const getCartCookies = (cookies: Cookies) => ({
  salesChannel: cookies?.['b2wChannel'],
  opn: cookies?.['b2wOpn'],
  epar: cookies?.['b2wEPar'],
  affiliatedID: cookies?.['b2wFranq'],
  salesSolution: cookies?.['salesSolution'],
  partnerInfo: {
    utm_medium: cookies?.['utm_medium'],
    utm_source: cookies?.['utm_source'],
    utm_campaign: cookies?.['utm_campaign'],
    utm_content: cookies?.['utm_content'],
  },
  ...(!!cookies?.['customer.id'] && {
    customer: {
      id: cookies?.['customer.id'],
      token: cookies?.['customer.api.token'],
    },
  }),
});

export const addProductToCart = ({
  addProductQuery,
  updateProductQuery,
  cartId,
  productId,
  cookies,
}: IAddProductToCartProps) => (offers: IOffersToAdd) => {
  const offersToAdd = offers?.offersToAdd ?? [];
  const offersToUpdate = offers?.offersToUpdate ?? [];

  // As acoes de adicionar e atualizar e devem ser chamadas separadamente, para nao ter incosistencia no cart
  if (offersToAdd?.length > 0) {
    sendRiskifiedBeaconAddToCart(productId);

    const cartCookies = getCartCookies(cookies);
    addProductQuery({
      variables: {
        cartId,
        lines: offersToAdd,
        ...cartCookies,
      },
    });
  } else if (offersToUpdate?.length > 0) {
    // Por enquanto, e possivel atualizar somente 1 produto por vez
    const item = offersToUpdate?.[0];
    updateProductQuery({
      variables: { cartId, lineId: item?.lineId, item },
    });
  }
};

export const getProductFromProps = (
  product: IProduct,
  productCache?: IProductCache,
  currentSku?: IProduct
): ITypeOfProduct => {
  if (productCache?.offer?.id) {
    return productCache;
  }

  if (currentSku) {
    return currentSku;
  }

  return product?.product ?? product;
};

export const getOfferId = (product: IProduct, productCache?: IProductCache, currentSku?: IProduct) => {
  const current = getProductFromProps(product, productCache, currentSku);
  if (current?.offer?.id) {
    return current?.offer?.id;
  }
  return current?.offers?.result?.[0]?.offerId ?? current?.offers?.result?.[0]?.id;
};

export const getProductId = (product: IProduct, productCache?: IProductCache) => {
  const current = getProductFromProps(product, productCache);
  return (current?.id ?? current?.product?.id) as string;
};
